import { formatReleaseTime } from '@home/components/NavHeader/sreachContext/utils';
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { getStdAUMetricName } from '@intelligence/v3/utils/metric';
import {
  buildDownloadDataNew as buildDownloadData,
  getRegionByCode,
  getRegionByCountryCode,
  getCountryByCode,
} from '@intelligence/utils/commonUtils';
import { DataCalculation } from '@intelligence/v3/enums/download';
import { LANGUAGE_EN } from '@home/_config/config';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';

const GRANULARITTIES: IGranularity[] = ['daily', 'weekly', 'monthly'];

const GRANULARITY_NAME_MAP: IRecord<string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

const mergeGameDetailsData = (list?: any[] | null, detailsMap?: IRecord<IRecord> | null): IRecord[] => {
  if (isEmpty(list)) return [];
  if (isEmpty(detailsMap)) return list!;

  return list!.map((item) => {
    const gameId = item?.game_id || '';
    const gameDetails = detailsMap![gameId];
    return { ...item, ...gameDetails };
  });
};

const getGranularityListMap = (list?: any[]): IRecord<any[]> => {
  if (isEmpty(list)) return {};
  return list!.reduce((acc: IRecord<any[]>, cur: any) => {
    const listGranularity = cur?.$req?.granularity;

    if (!listGranularity) {
      return acc;
    }

    return { ...acc, [listGranularity]: mergeGameDetailsData(cur?.list, cur?.$ext?.entity_info) };
  }, {});
};

const getMarketNames = (markets?: string[] | null, regionOptions?: any[] | null): string => {
  if (isEmpty(markets)) return '';
  if (isEmpty(regionOptions)) return markets!.join(', ');

  const marketNames = markets!.map((market) => {
    return getCountryByCode(market, regionOptions!)?.country_name_en || '';
  });

  return marketNames.join(', ');
};

export const getMobileTopChartsMultiDataSourceConfig = (data: any, params: any, allParams: any) => {
  // console.debug('111 data: ', data);
  // console.debug('111 params: ', params);
  // console.debug('111 allParams: ', allParams);

  const granularities: IGranularity[] = allParams?.download_date_type || GRANULARITTIES;
  const isCompanyShow = allParams?.company_show === 1;
  const isGenreShow = allParams?.genre_show === 1;
  const showGenreSource = allParams?.genre_source || 'iegg';
  const isIEGGGenreShow = showGenreSource === 'iegg';
  const isAnnieGenreShow = showGenreSource === 'annie';
  const isSensorTowerGenreShow = showGenreSource === 'sensortower';
  const isAppMagicGenreShow = showGenreSource === 'appmagic';
  const isReleaseDateShow = allParams?.release_date_show === 1;
  const isGameEngineShow = allParams?.game_engine_show === 1;
  const isShowRatio = allParams?.ratio_show === 1;
  const regionOptions = allParams?.regionOptions || [];
  const isGroupByDate = allParams?.downlaod_date_method !== DataCalculation.Aggregated;
  const isGroupByRegion = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByRegion;
  const isGroupByMarket = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByCountry;

  // 需要下载的指标映射
  const metricMap: IRecord<boolean> = params?.metrics?.reduce((acc: IRecord<boolean>, cur: string) => {
    return { ...acc, [cur]: true };
  }, {});

  const publisherAndDeveloperConfig = [
    {
      title: 'Publisher Company Name',
      key: 'publisher',
      when: () => isCompanyShow,
    },
    {
      title: 'Publisher Company HQ Name',
      key: 'country_name_en',
      when: () => isCompanyShow,
    },
    {
      title: 'Publisher Parent Company',
      key: 'parent_publisher',
      when: () => isCompanyShow,
    },
    {
      title: 'Developer Company Name',
      key: 'developer',
      when: () => isCompanyShow,
    },
    {
      title: 'Developer Company HQ Name',
      key: 'developer_country_name_en',
      when: () => isCompanyShow,
    },
    {
      title: 'Developer Parent Company',
      key: 'developer_parent',
      when: () => isCompanyShow,
    },
  ];

  const genreConfig = [
    {
      title: 'App Magic Super Genre',
      key: 'super_genre',
      when: () => isGenreShow && isAppMagicGenreShow,
    },
    {
      title: 'App Magic Level-1 Genre',
      key: 'major_category',
      when: () => isGenreShow && isAppMagicGenreShow,
    },
    {
      title: 'App Magic Level-2 Genre',
      key: 'mid_category',
      when: () => isGenreShow && isAppMagicGenreShow,
    },
    {
      title: 'App Magic Level-3 Genre',
      key: 'sub_category',
      when: () => isGenreShow && isAppMagicGenreShow,
    },
    {
      title: 'IEGG Main Genre',
      key: 'iegg_genre',
      when: () => isGenreShow && isIEGGGenreShow,
    },
    {
      title: 'IEGG Sub Genre',
      key: 'iegg_sub_genre',
      when: () => isGenreShow && isIEGGGenreShow,
    },
    {
      title: 'App Annie Main Genre',
      key: 'genre',
      when: () => isGenreShow && isAnnieGenreShow,
    },
    {
      title: 'App Annie Sub Genre',
      key: 'sub_genre',
      when: () => isGenreShow && isAnnieGenreShow,
    },
    {
      title: 'Sensor Tower Main Genre',
      key: 'sensortower_genre',
      when: () => isGenreShow && isSensorTowerGenreShow,
    },
    {
      title: 'Sensor Tower Sub Genre',
      key: 'sensortower_sub_genre',
      when: () => isGenreShow && isSensorTowerGenreShow,
    },
  ];

  const regionCountryConfig = [
    {
      title: 'Region Chinese',
      key: (item: any) => {
        const itemRegion = item?.region || '';
        const itemMarket = item?.market || '';
        if (isGroupByRegion) return getRegionByCode(itemRegion, regionOptions)?.area_name_ch || itemRegion || '';
        if (isGroupByMarket) return getRegionByCountryCode(itemMarket, regionOptions)?.area_name_ch || itemMarket || '';
        return item?.market || item?.region || '';
      },
      when: () => isGroupByRegion || isGroupByMarket,
    },
    {
      title: 'Region English',
      key: (item: any) => {
        const itemRegion = item?.region || '';
        const itemMarket = item?.market || '';
        if (isGroupByRegion) return getRegionByCode(itemRegion, regionOptions)?.area_name_en || itemRegion || '';
        if (isGroupByMarket) return getRegionByCountryCode(itemMarket, regionOptions)?.area_name_en || itemMarket || '';
        return item?.market || item?.region || '';
      },
      when: () => isGroupByRegion || isGroupByMarket,
    },
    {
      title: 'Country Chinese',
      key: (item: any) => getCountryByCode(item?.market, regionOptions)?.country_name_ch || item?.market || '',
      when: () => isGroupByMarket,
    },
    {
      title: 'Country English',
      key: (item: any) => getCountryByCode(item?.market, regionOptions)?.country_name_en || item?.market || '',
      when: () => isGroupByMarket,
    },
  ];

  const buildCommonSheetConfig = (granularity: IGranularity) => {
    const stdAUMetricName = getStdAUMetricName(LANGUAGE_EN, granularity);

    return [
      {
        title: 'Game Name',
        key: 'game_name',
      },
      ...publisherAndDeveloperConfig,
      ...genreConfig,
      {
        title: 'Release Date',
        key: (item: any) => {
          const isSpecialReleaseTime = item?.is_special_release_time;
          const releaseTime = item?.release_time;
          const releaseTimeEN = item?.release_time_en;
          const releaseTimeCN = item?.release_time_cn;
          return formatReleaseTime(LANGUAGE_EN, {
            isSpecialReleaseTime,
            releaseTime,
            releaseTimeEN,
            releaseTimeCN,
          });
        },
        when: () => isReleaseDateShow,
      },
      {
        title: 'Date',
        key: (item: any) => {
          const itemDate = item?.date || '';
          return granularity === 'monthly' ? formatDateStr(itemDate, 'YYYY-MM') : itemDate;
        },
        when: () => isGroupByDate,
      },
      ...regionCountryConfig,
      {
        title: 'Game Engine',
        key: (item: any) => {
          const gameEngine = item?.game_engine;
          if (Array.isArray(gameEngine)) {
            return gameEngine.join('|');
          }
          return gameEngine || '';
        },
        when: () => isGameEngineShow,
      },
      {
        title: `${stdAUMetricName}(sensortower)`,
        key: 'sensortower__au',
        when: () => metricMap?.sensortower__au,
      },
      {
        title: '%',
        key: 'sensortower__au_ratio',
        when: () => isShowRatio && metricMap?.sensortower__au,
      },
      {
        title: 'Revenue(sensortower)',
        key: 'sensortower__revenue',
        when: () => metricMap?.sensortower__revenue,
      },
      {
        title: '%',
        key: 'sensortower__revenue_ratio',
        when: () => isShowRatio && metricMap?.sensortower__revenue,
      },
      {
        title: 'ARPU(sensortower)',
        key: 'sensortower__arpu',
        when: () => metricMap?.sensortower__arpu,
      },
      {
        title: '%',
        key: 'sensortower__arpu_ratio',
        when: () => isShowRatio && metricMap?.sensortower__arpu,
      },
      {
        title: 'Downloads(sensortower)',
        key: 'sensortower__download',
        when: () => metricMap?.sensortower__download,
      },
      {
        title: '%',
        key: 'sensortower__download_ratio',
        when: () => isShowRatio && metricMap?.sensortower__download,
      },
      {
        title: `${stdAUMetricName}(data.ai)`,
        key: 'appannie__au',
        when: () => metricMap?.appannie__au,
      },
      {
        title: '%',
        key: 'appannie__au_ratio',
        when: () => isShowRatio && metricMap?.appannie__au,
      },
      {
        title: 'Revenue(data.ai)',
        key: 'appannie__revenue',
        when: () => metricMap?.appannie__revenue,
      },
      {
        title: '%',
        key: 'appannie__revenue_ratio',
        when: () => isShowRatio && metricMap?.appannie__revenue,
      },
      {
        title: 'ARPU(data.ai)',
        key: 'appannie__arpu',
        when: () => metricMap?.appannie__arpu,
      },
      {
        title: '%',
        key: 'appannie__arpu_ratio',
        when: () => isShowRatio && metricMap?.appannie__arpu,
      },
      {
        title: 'Downloads(data.ai)',
        key: 'appannie__download',
        when: () => metricMap?.appannie__download,
      },
      {
        title: '%',
        key: 'appannie__download_ratio',
        when: () => isShowRatio && metricMap?.appannie__download,
      },
      {
        title: `${stdAUMetricName}(databrain)`,
        key: 'ogdb__au',
        when: () => metricMap?.ogdb__au,
      },
      {
        title: '%',
        key: 'ogdb__au_ratio',
        when: () => isShowRatio && metricMap?.ogdb__au,
      },
      {
        title: 'Revenue(databrain)',
        key: 'ogdb__revenue',
        when: () => metricMap?.ogdb__revenue,
      },
      {
        title: '%',
        key: 'ogdb__revenue_ratio',
        when: () => isShowRatio && metricMap?.ogdb__revenue,
      },
      {
        title: 'ARPU(databrain)',
        key: 'ogdb__arpu',
        when: () => metricMap?.ogdb__arpu,
      },
      {
        title: '%',
        key: 'ogdb__arpu_ratio',
        when: () => isShowRatio && metricMap?.ogdb__arpu,
      },
      {
        title: 'Downloads(databrain)',
        key: 'ogdb__download',
        when: () => metricMap?.ogdb__download,
      },
      {
        title: '%',
        key: 'ogdb__download_ratio',
        when: () => isShowRatio && metricMap?.ogdb__download,
      },
    ];
  };

  const dynamicSheets = (() => {
    const granularityListMap = getGranularityListMap(data);

    if (granularities?.length === 1) {
      const granularity = granularities[0];
      const commonSheetConfig = buildCommonSheetConfig(granularity);
      const granularityList = granularityListMap?.[granularity] || [];
      return {
        stat: {
          sheetName: 'Stats',
          style: 'normal',
          data: buildDownloadData(commonSheetConfig, granularityList),
        },
      };
    }

    return granularities?.reduce((acc, cur) => {
      const commonSheetConfig = buildCommonSheetConfig(cur);
      const curSheet = {
        sheetName: GRANULARITY_NAME_MAP[cur] || cur,
        style: 'normal',
        data: buildDownloadData(commonSheetConfig, granularityListMap?.[cur] || []),
      };
      return { ...acc, [cur]: curSheet };
    }, {});
  })();

  return {
    ...dynamicSheets,
    summary: {
      sheetName: `Summary`,
      style: 'summary',
      data: [
        ['Regions/Countries', getMarketNames(params?.markets, regionOptions)],
        ['Date Start', params?.start_date || ''],
        ['Date End', params?.end_date || ''],
      ],
    },
  };
};
